import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { WhitelabelService } from '../services/whitelabel.service';
import { whitelabelOrgCode } from '../helpers/whitelabel-theme';
import { environment } from 'src/environments/environment';

/**
 * Pre-login pages (landing, academy, login, register) open only when the whitelabel
 * details response has is_enabled: true; otherwise (or if the call fails) the visitor
 * gets the "Unauthorised access" page. Routes with data.landingPage (the landing and
 * academy pages) redirect to /login when the response carries an organization.code.
 * Routes with data.bykLoginFirst (the landing page) also redirect to /login on the
 * Biyaheko (BYK) build, which has no pre-login home page.
 */
@Injectable({ providedIn: 'root' })
export class WhitelabelDetailsGuard {
    constructor(
        private router: Router,
        private whitelabelService: WhitelabelService,
    ) { }

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        const unavailable = this.router.createUrlTree(['/site-unavailable']);
        return this.whitelabelService.getWhitelabelDetails().pipe(
            map((res: any) => {
                if (!WhitelabelDetailsGuard.isEnabled(res)) return unavailable;
                // an organisation's own site (it has a code) has no landing page: straight to login
                if (route.data?.['landingPage'] && WhitelabelDetailsGuard.hasOrganizationCode(res)) {
                    return this.router.createUrlTree(['/login']);
                }
                // Biyaheko (BYK): no landing page before login
                if (route.data?.['bykLoginFirst'] && environment?.clientAbbr === 'BYK') {
                    return this.router.createUrlTree(['/login']);
                }
                return true;
            }),
            catchError(() => of(unavailable))
        );
    }

    // the site is open only when the response says is_enabled: true
    // (also used by the "Unauthorised access" page to re-check on load)
    static isEnabled(res: any): boolean {
        return (res?.is_enabled ?? res?.data?.is_enabled) === true;
    }

    private static hasOrganizationCode(res: any): boolean {
        return whitelabelOrgCode(res) !== '';
    }
}
